import type { ShopperExperience, ShopperProducts } from '@/scapi';
import { type MenuItemIconId, parseMenuItemSideIcon } from '@/components/page-designer/menu-item/menu-item-icons';
import {
    type MenuItemTextVariant,
    parseMenuItemIconSizePx,
    parseMenuItemRemoveHorizontalPadding,
    parseMenuItemTextVariant,
} from '@/components/page-designer/menu-item/menu-item-config';

export const GLOBAL_MENU_ITEM_LISTS_REGION_ID = 'menuItemLists';
export const MENU_ITEM_LIST_ITEMS_REGION_ID = 'items';

/**
 * Earlier cartridge generations emitted the region constant's identifier name instead of its
 * value, so existing Page Designer content may store the nested items under this legacy id.
 * Kept for backward compatibility so previously saved content keeps rendering.
 */
const LEGACY_MENU_ITEM_LIST_ITEMS_REGION_ID = 'MENU_ITEM_LIST_ITEMS_REGION_ID';

export function findMenuItemListItemsRegion(
    component: ShopperExperience.schemas['Component']
): ShopperExperience.schemas['Region'] | undefined {
    return component.regions?.find(
        (region) => region.id === MENU_ITEM_LIST_ITEMS_REGION_ID || region.id === LEGACY_MENU_ITEM_LIST_ITEMS_REGION_ID
    );
}

export type MenuItemOverrideEntry = {
    id: string;
    label: string;
    to?: string;
    leftIcon?: MenuItemIconId;
    rightIcon?: MenuItemIconId;
    showTopBorder?: boolean;
    showBottomBorder?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    textWeight?: 'normal' | 'medium';
    textVariant?: MenuItemTextVariant;
    iconSizePx?: number;
    removeHorizontalPadding?: boolean;
    width?: string;
    backgroundColor?: string;
    hoverColor?: string;
};

export type MenuItemListOverrides = Record<string, MenuItemOverrideEntry[]>;

function getDataRecord(data: unknown): Record<string, unknown> {
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

function endsWithTypeId(typeId: string, shortId: string): boolean {
    return typeId === shortId || typeId.endsWith(`.${shortId}`);
}

function isMenuItemListComponent(typeId: string): boolean {
    return endsWithTypeId(typeId, 'menuItemList');
}

function isMenuItemComponent(typeId: string): boolean {
    return endsWithTypeId(typeId, 'menuItem');
}

function parseMenuItemEntry(component: ShopperExperience.schemas['Component']): MenuItemOverrideEntry | null {
    const data = getDataRecord(component.data);
    const label = typeof data.label === 'string' ? data.label.trim() : '';

    if (!label) {
        return null;
    }

    return {
        id: component.id,
        label,
        to: typeof data.to === 'string' && data.to.trim() ? data.to.trim() : undefined,
        leftIcon: parseMenuItemSideIcon(data, 'left'),
        rightIcon: parseMenuItemSideIcon(data, 'right'),
        showTopBorder: data.showTopBorder === true,
        showBottomBorder: data.showBottomBorder === true,
        textAlign:
            data.textAlign === 'left' || data.textAlign === 'center' || data.textAlign === 'right'
                ? data.textAlign
                : undefined,
        textWeight: data.textWeight === 'normal' || data.textWeight === 'medium' ? data.textWeight : undefined,
        textVariant: parseMenuItemTextVariant(data.textVariant),
        iconSizePx: parseMenuItemIconSizePx(data.iconSizePx),
        removeHorizontalPadding: parseMenuItemRemoveHorizontalPadding(data.removeHorizontalPadding) ? true : undefined,
        width: typeof data.width === 'string' ? data.width : undefined,
        backgroundColor: typeof data.backgroundColor === 'string' ? data.backgroundColor : undefined,
        hoverColor: typeof data.hoverColor === 'string' ? data.hoverColor : undefined,
    };
}

function parseMenuItemListComponent(component: ShopperExperience.schemas['Component']): MenuItemListOverrides {
    const data = getDataRecord(component.data);
    const categoryId = typeof data.categoryId === 'string' ? data.categoryId.trim() : '';

    if (!categoryId) {
        return {};
    }

    const itemsRegion = findMenuItemListItemsRegion(component);
    const items =
        itemsRegion?.components
            ?.filter((item) => isMenuItemComponent(item.typeId))
            .map(parseMenuItemEntry)
            .filter((item): item is MenuItemOverrideEntry => item !== null) ?? [];

    if (items.length === 0) {
        return {};
    }

    return { [categoryId]: items };
}

/**
 * Reads stacked menu-item-list components from the burger menu content slots page and maps
 * each list to its configured category ID.
 */
export function parseMenuItemListOverrides(
    page: ShopperExperience.schemas['Page'] | null | undefined
): MenuItemListOverrides {
    const overrides: MenuItemListOverrides = {};
    const region = page?.regions?.find((entry) => entry.id === GLOBAL_MENU_ITEM_LISTS_REGION_ID);

    if (!region?.components?.length) {
        return overrides;
    }

    for (const component of region.components) {
        if (!isMenuItemListComponent(component.typeId)) {
            continue;
        }

        const parsed = parseMenuItemListComponent(component);
        for (const [categoryId, items] of Object.entries(parsed)) {
            overrides[categoryId] = items;
        }
    }

    return overrides;
}

export function isMenuOverridden(category: ShopperProducts.schemas['Category']): boolean {
    return Boolean(category.c_isMenuOverridden);
}

export function getMenuOverrideItems(
    categoryId: string,
    overrides: MenuItemListOverrides
): MenuItemOverrideEntry[] | undefined {
    const items = overrides[categoryId];
    return items?.length ? items : undefined;
}
